<script>
  import * as Pancake from "@sveltejs/pancake";
  import { tweened } from "svelte/motion";
  import * as eases from "svelte/easing";
  import { fade } from "svelte/transition";
  import PancakeChart from "./Pancake.svelte";
  import { treemap, hierarchy } from "d3-hierarchy";
  import {
    budgetBlocksTreeDepts,
    actualSelectedBudgetTreeDepts,
    selectedWithHighestUnselected,
    otherDeptsGrouped,
    selectedDepartmentsSorted,
    explore,
  } from "@stores/treemap.js";
  import { budgetBlocks } from "@stores/userData.js";
  import colorScale from "@helpers/departmentTypeColorScale.js";

  $: displayData = {
    name: "Houston Budget",
    level: "root",
    children: $explore,
  };

  $: treemapper = treemap();

  $: hierarch = hierarchy(displayData).sum((d) => d.value);
  $: hierarch.eachBefore((node) => {
    if (!node.parent) {
      return;
    }
    if (node.parent.data.isSelected !== undefined) {
      node.data.isSelected = node.parent.data.isSelected;
    }
    if (node.parent.data.departmentType !== undefined) {
      node.data.departmentType = node.parent.data.departmentType;
    }
  });
  $: hierarch.eachAfter((node) => {
    if (node.children) {
      node.data.amount = node.children.reduce(
        (prev, child) => prev + (child.data.amount || child.value),
        0
      );
    }
  });
  $: hierarch.sort((a, b) => {
    return b.data.amount - a.data.amount;
  });

  $: root = treemapper(hierarch);

  $: selected = root;

  $: select = (node) => {
    if (node.data.level !== "department") {
      while (node.parent && node.parent !== selected) {
        node = node.parent;
      }
    }

    if (node && node.children) selected = node;
  };

  $: extents = tweened(undefined, {
    easing: eases.cubicOut,
    duration: 600,
  });

  $: is_visible = (a, b) => {
    // show only department level until step 4
    // show only department level at root of step 4; hide department type nodes
    if (b.depth === 0) {
      return a.data.level === "department";
    }
    while (b) {
      if (a.parent === b) {
        return true;
      }
      b = b.parent;
    }

    return false;
  };

  $: $extents = {
    x1: selected.x0,
    x2: selected.x1,
    y1: selected.y1,
    y2: selected.y0,
  };

  import { format } from "d3-format";
  const formatNum = (num) => format(",.2~f")(num);

  // Lookup node size and return true if width or height is less than .1
  $: isSmall = (node) => {
    // Update this whenever the user zooms in to a new level
    // We do this via the extents object, where extents.x2 and extents.y2 represent bounds
    const treemapWidth = $extents.x2 - $extents.x1;
    const nodeWidth = node.x1 - node.x0;
    const width = nodeWidth / treemapWidth;

    const treemapHeight = $extents.y1 - $extents.y2;
    const nodeHeight = node.y1 - node.y0;
    const height = nodeHeight / treemapHeight;

    return width < 0.125 || height < 0.1;
  };

  $: topFourDepartmentsSorted = $budgetBlocks
    .sort((a, b) => {
      return b.items.length - a.items.length;
    })
    .map((d) => d.name);

  import data from "@data/departmentBudgets.json";
  const suffixFormat = format(",.3s");

  import { sum } from "d3-array";
  $: sumOfSelected = sum($selectedDepartmentsSorted.map((dept) => dept.amount));

  const sumOfAll = sum(data.map((d) => d.budget));
  const percentFormat = format(".2%");
  $: percentOfTotal = sumOfSelected / sumOfAll;

  export let downloadable;
</script>

<div class="hidden-container">
  <div class="shareable-container" bind:this={downloadable}>
    <span class="credit">houstonbudget.cool</span>
    <h1 class="title">Do you know what's in the Houston Budget?</h1>
    <h2 class="subtitle">
      My top four departments ★, {topFourDepartmentsSorted[0]}, {topFourDepartmentsSorted[1]},
      {topFourDepartmentsSorted[2]}, and {topFourDepartmentsSorted[3]},
      <strong
        >made up {percentFormat(percentOfTotal)} of the General Fund budget in FY2022.</strong
      >
    </h2>
    <div class="chart">
      <Pancake.Chart
        x1={$extents.x1}
        x2={$extents.x2}
        y1={$extents.y1}
        y2={$extents.y2}
      >
        <PancakeChart {root} let:node>
          {#if is_visible(node, selected)}
            <div
              transition:fade={{ duration: 400 }}
              class="node {node.data.level} {!node.data.isSelected
                ? 'noise'
                : ''}"
              class:leaf={!node.children}
              class:has_children={node.children}
            >
              <div
                class="contents {!node.data.isSelected ? 'inactive' : 'active'}"
                class:small={isSmall(node)}
                style="background-color: {colorScale(node.data.departmentType)}"
              >
                <h1>{node.data.name}</h1>
                <h2>
                  {(node.data.mode === "budget-blocks" && `${node.value}/10`) ||
                    `$${formatNum(node.value)}`}
                </h2>
              </div>
            </div>
          {/if}
        </PancakeChart>
      </Pancake.Chart>
    </div>
  </div>
</div>

<style>
  .hidden-container {
    transform: translateX(100vw);
    width: 0;
    height: 0;
  }

  .shareable-container {
    width: 1300px;
    height: 740px;
    background: white;
    padding: 1rem;
    position: relative;
  }

  .chart {
    width: 100%;
    margin: 0 auto;
    height: 600px;
    margin-bottom: 0.75rem;
    padding: 0;
    overflow: hidden;
  }

  .node {
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: white;
    overflow: hidden;
    pointer-events: all;
  }

  .node:not(.leaf) {
    cursor: pointer;
  }

  .contents {
    width: 100%;
    height: 100%;
    padding: 0.5rem;
    opacity: 0.7;
    border: 1px solid white;
    border-radius: 4px;
    box-sizing: border-box;
    overflow: hidden;
    transition: opacity 500ms ease, border 500ms ease;
    position: relative;
  }

  .contents,
  .contents * {
    color: white;
  }

  .has_children:hover .contents {
    opacity: 1;
  }

  .contents h1 {
    font-size: 1.5rem;
    margin-bottom: 0.35rem;
    transition: opacity 400ms ease;
    font-weight: 700;
    text-shadow: 1px 2px 3px rgba(0, 0, 0, 0.35);
    /* font-family: var(--font-serif); */
  }

  h2 {
    font-size: 1.25rem;
    font-weight: 200;
    transition: opacity 400ms ease;
  }

  .small h1 {
    font-size: 1rem;
  }
  .small h2 {
    font-size: 0.85rem;
  }

  /* .node.noise::before {
    width: 100%;
    height: 100%;
    content: "";
    position: absolute;
    pointer-events: none;
    background: #efefef
      url(data:image/png;base64,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);
  } */

  .active {
    border: 1px solid white;
    opacity: 1;
  }

  .active::after {
    content: "\2605";
    position: absolute;
    bottom: 3px;
    right: 3px;
    font-size: 1rem;
    text-shadow: 1px 2px 3px rgba(0, 0, 0, 0.15);
    color: white;
  }

  .title {
    font-size: 2rem;
    /* font-family: var(--font-serif); */
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  .subtitle {
    line-height: 1.25;
    font-size: 1.25rem;
    margin-bottom: 1rem;
    font-weight: 300;
  }

  .credit {
    position: absolute;
    top: 1rem;
    right: 1rem;
    text-transform: uppercase;
    font-size: 1rem;
    font-weight: 200;
    font-style: italic;
  }
</style>
